import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, IconButton, Link, Stack, Typography } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { bonusTrackerAPI } from '../../services/api';
import { BonusAttachment, BonusDetail, errText } from './bonusTypes';

interface Props {
  recordId: string;
  attachments: BonusAttachment[];
  canAttach: boolean;
  onDetail: (detail: BonusDetail) => void;
}

const size = (bytes: number) => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

/** A screenshot thumbnail, loaded with the user's login (the file route is not public). */
const Thumb: React.FC<{ recordId: string; file: BonusAttachment }> = ({ recordId, file }) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    let objectUrl: string | null = null;
    bonusTrackerAPI.attachmentBlob(recordId, file.id).then((blob) => {
      if (!alive) return;
      objectUrl = URL.createObjectURL(blob);
      setUrl(objectUrl);
    }).catch(() => undefined);
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [recordId, file.id]);
  return url
    ? <Box component="img" src={url} alt={file.fileName} sx={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider', cursor: 'pointer' }}
        onClick={() => window.open(url, '_blank', 'noopener')} />
    : <Box sx={{ width: 120, height: 90, bgcolor: 'action.hover', borderRadius: 1 }} />;
};

/**
 * The evidence for a bonus: documents that prove the bonus, and screenshots that prove the listed people qualify. Several files
 * of each kind. Supervisors and above add or remove them while the bonus can still be changed; adding evidence never clears an approval.
 */
const EvidenceSection: React.FC<Props> = ({ recordId, attachments, canAttach, onDetail }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const proofInput = useRef<HTMLInputElement>(null);
  const shotInput = useRef<HTMLInputElement>(null);
  const proofs = attachments.filter((a) => a.kind === 'PROOF');
  const shots = attachments.filter((a) => a.kind === 'SCREENSHOT');

  const upload = async (kind: 'PROOF' | 'SCREENSHOT', list: FileList | null) => {
    if (!list || !list.length) return;
    setBusy(true);
    setError(null);
    try {
      onDetail(await bonusTrackerAPI.uploadAttachments(recordId, kind, Array.from(list)));
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (file: BonusAttachment) => {
    if (!window.confirm(`Remove ${file.fileName}?`)) return;
    setBusy(true);
    setError(null);
    try {
      onDetail(await bonusTrackerAPI.deleteAttachment(recordId, file.id));
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const open = async (file: BonusAttachment) => {
    try {
      const blob = await bonusTrackerAPI.attachmentBlob(recordId, file.id);
      window.open(URL.createObjectURL(blob), '_blank', 'noopener');
    } catch (e) {
      setError(errText(e));
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Evidence</Typography>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

      <Stack spacing={2}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Proof of the bonus</Typography>
            {canAttach && (
              <>
                <Button size="small" variant="outlined" disabled={busy} onClick={() => proofInput.current?.click()}>Add files</Button>
                <input ref={proofInput} hidden multiple type="file" accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.heic,.txt,.csv,.xls,.xlsx,.doc,.docx"
                  onChange={(e) => { void upload('PROOF', e.target.files); e.target.value = ''; }} />
              </>
            )}
          </Stack>
          {!proofs.length && <Typography variant="caption" color="text.secondary">No files yet.</Typography>}
          {proofs.map((f) => (
            <Stack key={f.id} direction="row" alignItems="center" spacing={1}>
              <Link component="button" type="button" variant="body2" onClick={() => open(f)}>{f.fileName}</Link>
              <Typography variant="caption" color="text.secondary">{size(f.sizeBytes)} · {f.uploadedByName || ''}</Typography>
              {canAttach && <IconButton size="small" disabled={busy} onClick={() => remove(f)} aria-label="Remove file"><Delete fontSize="small" /></IconButton>}
            </Stack>
          ))}
        </Box>

        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Screenshots showing who qualifies</Typography>
            {canAttach && (
              <>
                <Button size="small" variant="outlined" disabled={busy} onClick={() => shotInput.current?.click()}>Add screenshots</Button>
                <input ref={shotInput} hidden multiple type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/heic"
                  onChange={(e) => { void upload('SCREENSHOT', e.target.files); e.target.value = ''; }} />
              </>
            )}
          </Stack>
          {!shots.length && <Typography variant="caption" color="text.secondary">No screenshots yet.</Typography>}
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {shots.map((f) => (
              <Box key={f.id} sx={{ position: 'relative' }}>
                <Thumb recordId={recordId} file={f} />
                {canAttach && (
                  <IconButton size="small" disabled={busy} onClick={() => remove(f)} aria-label="Remove screenshot"
                    sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.paper' } }}>
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
        <Typography variant="caption" color="text.secondary">Up to 10 MB per file. Adding or removing evidence does not change the approvals.</Typography>
      </Stack>
    </Box>
  );
};

export default EvidenceSection;
